import React from "react";
import { Link } from "react-router-dom";
import { Accessibility as AccIcon, Volume2, Eye, Phone, Mail } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { CtaBlock } from "../components/CtaBlock";
import { buildBreadcrumbLd } from "../components/Breadcrumbs";
import { useSeo } from "../lib/seo";

export default function AccessibilityPage() {
  const canonical = "https://westbridgehotels.com/accessibility";
  useSeo({
    title: "Accessibility & ADA — Westbridge Inn & Suites, Centerville, Iowa",
    description:
      "Substantially compliant with the 2010 ADA Standards. Accessible parking, entrance, and pool lift. Accessible King rooms with roll-in showers. TTY & visual kits on request.",
    canonical,
    jsonLd: [buildBreadcrumbLd([{ label: "Accessibility", to: "/accessibility" }])],
  });

  return (
    <main id="main" data-testid="accessibility-page">
      <PageHero
        eyebrow="Accessibility"
        title="Substantially compliant with the 2010 ADA Standards."
        intro="Westbridge Inn & Suites is committed to accommodating guests of every ability. Below is a plain-English summary of what we do — and how to reach us if you need something we haven't listed."
        breadcrumbs={[{ label: "Accessibility", to: "/accessibility" }]}
        testId="accessibility-hero"
        compact
      />

      {/* Property-wide accessibility */}
      <section className="bg-cream py-16 md:py-24">
        <div className="container-app grid md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-4">
            <AccIcon size={28} className="text-brass mb-4" strokeWidth={1.5} />
            <h2 className="font-display font-light text-3xl md:text-4xl text-forest-900 tracking-tight leading-tight">
              Property-wide access.
            </h2>
            <p className="mt-5 text-ink/70 text-base leading-relaxed">
              The property is in substantial compliance with the 2010 ADA Standards for Accessible Design.
            </p>
          </div>
          <div className="md:col-span-8">
            <ul className="space-y-4 text-ink/85 text-lg leading-relaxed">
              {[
                "Accessible self-parking spaces marked in the main lot near the accessible entrance.",
                "An accessible route from the accessible parking to the public entrance to the hotel.",
                "An accessible route from the public entrance to the registration desk.",
                "Registration desk with a lowered section that meets ADA counter-height requirements.",
                "Accessible route through the lobby to the pool, breakfast area, and meeting room.",
                "Indoor pool with an accessible ADA lift for safe transfer in and out of the water.",
                "Accessible restrooms in the public spaces where public restrooms are provided.",
                "Emergency exits accessible from every guest floor.",
              ].map((l) => (
                <li key={l} className="flex gap-3">
                  <span className="mt-2.5 w-2 h-2 rounded-full bg-brass shrink-0" />
                  <span>{l}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Accessible King rooms */}
      <section className="bg-cream-100 py-16 md:py-24 paper-grain">
        <div className="container-app relative grid md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-4">
            <p className="eyebrow mb-3">Accessible King Rooms</p>
            <h2 className="font-display font-light text-3xl md:text-4xl text-forest-900 tracking-tight leading-tight">
              In-room features.
            </h2>
            <Link to="/rooms/accessible-king" className="mt-6 inline-flex items-center gap-2 text-forest-900 brass-underline text-sm">
              See the Accessible King room →
            </Link>
          </div>
          <div className="md:col-span-8">
            <ul className="space-y-4 text-ink/85 text-lg leading-relaxed">
              {[
                "ADA-compliant doorway widths and clearances throughout the room.",
                "Roll-in shower with a securely fastened folding seat.",
                "ADA-compliant grab bars in the shower and around the toilet.",
                "Slip-resistant bathroom flooring and shower surfaces.",
                "Bed at accessible height with clear floor space around it.",
                "Hardware and controls operable with one hand and no tight grasping.",
                "Accessible power outlets and work surface with forward approach.",
                "Closed-captioning television.",
              ].map((l) => (
                <li key={l} className="flex gap-3">
                  <span className="mt-2.5 w-2 h-2 rounded-full bg-brass shrink-0" />
                  <span>{l}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Hearing / vision equipment */}
      <section className="bg-cream py-16 md:py-24">
        <div className="container-app grid md:grid-cols-2 gap-6 md:gap-10">
          <article className="bg-cream-100 border border-tan p-8 md:p-10">
            <Volume2 size={22} className="text-brass mb-4" strokeWidth={1.5} />
            <h3 className="font-display text-2xl md:text-3xl text-forest-900 mb-3">
              For guests who are hearing-impaired.
            </h3>
            <p className="text-ink/85 leading-relaxed">
              Communication kits with a TTY / TDD telephone, a bed-shaker, a strobe light for smoke alarm, and a visual door knocker are available on demand at the front desk. Portable equipment is provided free of charge.
            </p>
          </article>
          <article className="bg-cream-100 border border-tan p-8 md:p-10">
            <Eye size={22} className="text-brass mb-4" strokeWidth={1.5} />
            <h3 className="font-display text-2xl md:text-3xl text-forest-900 mb-3">
              For guests with low vision.
            </h3>
            <p className="text-ink/85 leading-relaxed">
              Accessible signage in public spaces. TTY-compatible phones. Front-desk staff are happy to read documents aloud, explain room layouts before check-in, and provide any assistance you need.
            </p>
          </article>
        </div>
      </section>

      {/* WCAG website statement */}
      <section className="bg-forest-900 text-cream py-16 md:py-24">
        <div className="container-app grid md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-4">
            <p className="eyebrow text-brass-300 mb-3">Website accessibility</p>
            <h2 className="font-display font-light text-3xl md:text-4xl tracking-tight leading-tight">
              WCAG 2.1 AA.
            </h2>
          </div>
          <div className="md:col-span-8 text-cream/85 text-lg leading-relaxed space-y-5">
            <p>
              This website targets WCAG 2.1 Level AA. We use semantic HTML, keyboard-navigable menus and dialogs, meaningful alt text on every image, and clear, high-contrast typography. If you encounter an accessibility barrier on the site, please let us know.
            </p>
            <p>
              For accessibility questions or to report an issue, please contact us:
            </p>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <a href="mailto:info@westbridgehotels.com" className="btn-outline text-base" data-testid="accessibility-email">
                <Mail size={14} /> info@westbridgehotels.com
              </a>
              <a href="tel:+16414377272" className="btn-outline text-base" data-testid="accessibility-phone">
                <Phone size={14} /> (641) 437-7272
              </a>
            </div>
          </div>
        </div>
      </section>

      <CtaBlock
        eyebrow="Booking an accessible room"
        title="Please call the front desk."
        body="Booking accessible rooms by phone lets us confirm the specific accommodations you need — roll-in shower, accessible route, kit rentals — before you drive out."
      />
    </main>
  );
}
